import React from 'react';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import parse from 'autosuggest-highlight/parse';
import throttle from 'lodash/throttle';
import { GOOGLE_API_KEY } from 'common/env';

const SCRIPT_ID = 'google-maps';
const PLACE_FIELDS = ['formattedAddress', 'location', 'id'];

function loadScript(src, position, id) {
    if (!position || document.getElementById(id)) {
        return;
    }

    const script = document.createElement('script');
    script.setAttribute('async', '');
    script.setAttribute('id', id);
    script.src = src;
    position.appendChild(script);
}

function normalizeSuggestion(suggestion) {
    const prediction = suggestion.placePrediction;

    if (!prediction) {
        return null;
    }

    return {
        placePrediction: prediction,
        placeId: prediction.placeId,
        description: prediction.text?.text ?? '',
        mainText: prediction.mainText,
        secondaryText: prediction.secondaryText
    };
}

const useStyles = makeStyles(theme => ({
    icon: {
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(2)
    }
}));

const Adresse = props => {
    const classes = useStyles();
    const { setAdresse, selectedAdresse } = props;
    const [value, setValue] = React.useState(selectedAdresse);
    const [inputValue, setInputValue] = React.useState('');
    const [options, setOptions] = React.useState([]);
    const loaded = React.useRef(false);

    React.useEffect(() => {
        if (typeof window === 'undefined' || loaded.current) {
            return;
        }

        loadScript(
            `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places`,
            document.head,
            SCRIPT_ID
        );

        loaded.current = true;
    }, []);

    const fetchSuggestions = React.useMemo(
        () =>
            throttle((request, callback) => {
                if (!window.google?.maps?.places?.AutocompleteSuggestion) {
                    callback(null);
                    return;
                }

                window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
                    request
                )
                    .then(({ suggestions }) => callback(suggestions))
                    .catch(() => callback(null));
            }, 200),
        []
    );

    React.useEffect(() => fetchSuggestions.cancel, [fetchSuggestions]);

    const setLocation = React.useCallback(
        newValue => {
            if (!newValue?.placePrediction) {
                return;
            }

            const place = newValue.placePrediction.toPlace();

            place
                .fetchFields({ fields: PLACE_FIELDS })
                .then(() => {
                    setAdresse({
                        target: {
                            name: 'adresse',
                            value: {
                                description: place.formattedAddress,
                                place_id: place.id,
                                coordinates: {
                                    lat: place.location.lat(),
                                    lng: place.location.lng()
                                }
                            }
                        }
                    });
                })
                .catch(err => {
                    console.error('Error fetching place details', err);
                });
        },
        [setAdresse]
    );

    React.useEffect(() => {
        let active = true;

        if (inputValue === '') {
            setOptions(value ? [value] : []);
            return undefined;
        }

        fetchSuggestions({ input: inputValue }, suggestions => {
            if (!active) {
                return;
            }

            const normalized = suggestions
                ? suggestions.map(normalizeSuggestion).filter(Boolean)
                : [];

            setOptions(value ? [value, ...normalized] : normalized);
        });

        return () => {
            active = false;
        };
    }, [value, inputValue, fetchSuggestions]);

    const handleChange = React.useCallback(
        (event, newValue) => {
            setLocation(newValue);
            setValue(newValue);
            setOptions(prev => (newValue ? [newValue, ...prev] : prev));
        },
        [setLocation]
    );

    const handleInputChange = React.useCallback((event, newInputValue) => {
        setInputValue(newInputValue);
    }, []);

    const getOptionLabel = React.useCallback(
        option => (typeof option === 'string' ? option : option.description),
        []
    );

    const filterOptions = React.useCallback(x => x, []);

    const renderInput = React.useCallback(
        params => (
            <TextField
                {...params}
                label="Adresse du marché"
                variant="outlined"
                helperText="Rechercher une adresse"
                fullWidth
            />
        ),
        []
    );

    const renderOption = React.useCallback(
        option => {
            const mainText = option.mainText?.text ?? option.description;
            const matches = option.mainText?.matches ?? [];
            const parts = parse(
                mainText,
                matches.map(match => [match.startOffset, match.endOffset])
            );

            return (
                <Grid container alignItems="center">
                    <Grid item>
                        <LocationOnIcon className={classes.icon} />
                    </Grid>
                    <Grid item xs>
                        {parts.map((part, index) => (
                            <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                                {part.text}
                            </span>
                        ))}

                        <Typography variant="body2" color="textSecondary">
                            {option.secondaryText?.text}
                        </Typography>
                    </Grid>
                </Grid>
            );
        },
        [classes.icon]
    );

    return (
        <Autocomplete
            getOptionLabel={getOptionLabel}
            filterOptions={filterOptions}
            options={options}
            autoComplete
            includeInputInList
            filterSelectedOptions
            value={value}
            noOptionsText="Aucune adresse n'a été trouvée"
            onChange={handleChange}
            onInputChange={handleInputChange}
            renderInput={renderInput}
            renderOption={renderOption}
        />
    );
};

export default Adresse;
